/**
 * useEvidenceViewer — REQ-090 state machine for the admin Evidence Viewer
 * module (ODE-P07-T05).
 *
 * Flow: `pending` (filters not yet submitted) → `submit()` → `loading` → the
 * guarded list outcome:
 *   200 → `ready` (server page; an empty `rows` array is the REQ-090 empty
 *         state rendered by the module, never a fabricated row)
 *   400 → `error`   (invalid filter — the server rejected it, retryable with
 *         corrected input)
 *   401 → `unauthorized`
 *   403 → `blocked` (server-enforced; the client mirror is UX only)
 *   503/network → `error`
 *
 * Detail (`openDetail(evidenceId)`) is a separate read-only projection with its
 * own state (`idle`/`loading`/`ready`/`not_found`/`unauthorized`/`blocked`/
 * `error`). The REQ-063 retention-until-ack flag, the acknowledgement state and
 * the REQ-078 short-lived media reference all come from the server verbatim.
 *
 * Media expiry is a *presentation* concern: `isMediaExpired` compares the
 * server-issued `expiresAt` with the current clock, and `refreshDetail()`
 * re-fetches the detail so the server re-mints a fresh reference. The client
 * never fabricates a URL and never extends one.
 *
 * `loadMore()` appends the next keyset page (the cursor is opaque; the client
 * never builds one).
 *
 * Traces to: ODE-PRD-REQ-047, ODE-PRD-REQ-063, ODE-PRD-REQ-078,
 *            ODE-PRD-REQ-090.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  api,
  type AdminEvidenceDetail,
  type AdminEvidenceDetailResult,
  type AdminEvidenceListResult,
  type AdminEvidencePage,
  type AdminEvidenceQuery,
  type EvidenceAckState,
} from '../../api/client.js';

/** The raw filter form (all values are strings; empties are omitted). */
export interface EvidenceViewerForm {
  readonly deliveryOrderId: string;
  readonly checkpointId: string;
  readonly evidenceType: string;
  readonly ackState: '' | EvidenceAckState;
  readonly limit: string;
}

export const EMPTY_EVIDENCE_FORM: EvidenceViewerForm = {
  deliveryOrderId: '',
  checkpointId: '',
  evidenceType: '',
  ackState: '',
  limit: '',
};

export type EvidenceListState =
  | { readonly kind: 'pending' }
  | { readonly kind: 'loading' }
  | { readonly kind: 'ready'; readonly page: AdminEvidencePage; readonly loadingMore: boolean }
  | { readonly kind: 'unauthorized' }
  | { readonly kind: 'blocked'; readonly message: string }
  | { readonly kind: 'error'; readonly message: string };

export type EvidenceDetailState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'loading'; readonly evidenceId: string }
  | { readonly kind: 'ready'; readonly detail: AdminEvidenceDetail }
  | { readonly kind: 'not_found' }
  | { readonly kind: 'unauthorized' }
  | { readonly kind: 'blocked'; readonly message: string }
  | { readonly kind: 'error'; readonly message: string };

export interface UseEvidenceViewerOptions {
  readonly token: string | null;
  /** Test seam: list fetch (default: the shared api client). */
  readonly list?:
    | ((query: AdminEvidenceQuery, token: string) => Promise<AdminEvidenceListResult>)
    | undefined;
  /** Test seam: detail fetch (default: the shared api client). */
  readonly detail?:
    | ((evidenceId: string, token: string) => Promise<AdminEvidenceDetailResult>)
    | undefined;
}

export interface UseEvidenceViewerResult {
  readonly form: EvidenceViewerForm;
  readonly setForm: (patch: Partial<EvidenceViewerForm>) => void;
  readonly submit: () => void;
  readonly reset: () => void;
  readonly loadMore: () => void;
  readonly state: EvidenceListState;
  readonly detailState: EvidenceDetailState;
  readonly openDetail: (evidenceId: string) => void;
  readonly refreshDetail: () => void;
  readonly closeDetail: () => void;
}

/** Maps the raw form to the wire query (empties omitted, limit as a number). */
export function toEvidenceQuery(form: EvidenceViewerForm): AdminEvidenceQuery {
  const query: Record<string, string | number> = {};
  if (form.deliveryOrderId.trim() !== '') query.deliveryOrderId = form.deliveryOrderId.trim();
  if (form.checkpointId.trim() !== '') query.checkpointId = form.checkpointId.trim();
  if (form.evidenceType.trim() !== '') query.evidenceType = form.evidenceType.trim();
  if (form.ackState !== '') query.ackState = form.ackState;
  const limit = Number.parseInt(form.limit, 10);
  if (form.limit.trim() !== '' && Number.isInteger(limit)) query.limit = limit;
  return query;
}

/**
 * True when the server-issued media reference has elapsed. A missing reference
 * or an unparseable expiry is treated as expired (never a false "fresh").
 */
export function isMediaExpired(
  detail: AdminEvidenceDetail | null,
  nowMs: number,
): boolean {
  if (detail === null || detail.media === null) return false;
  const expiresAtMs = Date.parse(detail.media.expiresAt);
  if (Number.isNaN(expiresAtMs)) return true;
  return nowMs >= expiresAtMs;
}

export function useEvidenceViewer(
  options: UseEvidenceViewerOptions,
): UseEvidenceViewerResult {
  const { token } = options;
  const [form, setFormState] = useState<EvidenceViewerForm>(EMPTY_EVIDENCE_FORM);
  const [state, setState] = useState<EvidenceListState>({ kind: 'pending' });
  const [detailState, setDetailState] = useState<EvidenceDetailState>({ kind: 'idle' });
  const [request, setRequest] = useState<{
    readonly attempt: number;
    readonly query: AdminEvidenceQuery;
  } | null>(null);
  const attemptRef = useRef(0);
  const stateRef = useRef<EvidenceListState>(state);
  stateRef.current = state;

  const listRef = useRef(
    options.list ?? ((query: AdminEvidenceQuery, tokenValue: string) => api.listEvidence(query, tokenValue)),
  );
  listRef.current =
    options.list ??
    ((query: AdminEvidenceQuery, tokenValue: string) => api.listEvidence(query, tokenValue));
  const detailRef = useRef(
    options.detail ?? ((evidenceId: string, tokenValue: string) => api.fetchEvidenceDetail(evidenceId, tokenValue)),
  );
  detailRef.current =
    options.detail ??
    ((evidenceId: string, tokenValue: string) => api.fetchEvidenceDetail(evidenceId, tokenValue));

  const setForm = useCallback((patch: Partial<EvidenceViewerForm>) => {
    setFormState((current) => ({ ...current, ...patch }));
  }, []);

  const submit = useCallback(() => {
    attemptRef.current += 1;
    setRequest({ attempt: attemptRef.current, query: toEvidenceQuery(form) });
  }, [form]);

  const reset = useCallback(() => {
    setFormState(EMPTY_EVIDENCE_FORM);
    setRequest(null);
    setState({ kind: 'pending' });
    setDetailState({ kind: 'idle' });
  }, []);

  useEffect(() => {
    if (request === null) return undefined;
    if (token === null) {
      setState({ kind: 'unauthorized' });
      return undefined;
    }
    let cancelled = false;
    setState({ kind: 'loading' });
    listRef
      .current(request.query, token)
      .then((result) => {
        if (cancelled) return;
        setState(mapListResult(result));
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setState({
          kind: 'error',
          message: error instanceof Error ? error.message : 'Kesalahan jaringan',
        });
      });
    return () => {
      cancelled = true;
    };
  }, [token, request]);

  const loadMore = useCallback(() => {
    const current = stateRef.current;
    if (current.kind !== 'ready' || !current.page.hasMore || current.page.nextCursor === null) {
      return;
    }
    const cursor = current.page.nextCursor;
    const baseQuery = request?.query ?? {};
    setState({ ...current, loadingMore: true });
    void (async () => {
      if (token === null) return;
      try {
        const result = await listRef.current({ ...baseQuery, cursor }, token);
        if (result.status === 200 && result.payload !== null) {
          setState((latest) => {
            if (latest.kind !== 'ready') return latest;
            return {
              kind: 'ready',
              loadingMore: false,
              page: {
                ...result.payload!,
                rows: [...latest.page.rows, ...result.payload!.rows],
              },
            };
          });
        } else {
          setState({ kind: 'error', message: result.message ?? `HTTP ${result.status}` });
        }
      } catch (error: unknown) {
        setState({
          kind: 'error',
          message: error instanceof Error ? error.message : 'Kesalahan jaringan',
        });
      }
    })();
  }, [request, token]);

  const fetchDetail = useCallback(
    (evidenceId: string) => {
      if (token === null) {
        setDetailState({ kind: 'unauthorized' });
        return;
      }
      setDetailState({ kind: 'loading', evidenceId });
      void detailRef
        .current(evidenceId, token)
        .then((result) => {
          if (result.status === 200 && result.payload !== null) {
            setDetailState({ kind: 'ready', detail: result.payload });
          } else if (result.status === 404) {
            setDetailState({ kind: 'not_found' });
          } else if (result.status === 401) {
            setDetailState({ kind: 'unauthorized' });
          } else if (result.status === 403) {
            setDetailState({ kind: 'blocked', message: result.message ?? 'Akses ditolak.' });
          } else {
            setDetailState({ kind: 'error', message: result.message ?? `HTTP ${result.status}` });
          }
        })
        .catch((error: unknown) => {
          setDetailState({
            kind: 'error',
            message: error instanceof Error ? error.message : 'Kesalahan jaringan',
          });
        });
    },
    [token],
  );

  const openDetail = useCallback(
    (evidenceId: string) => {
      fetchDetail(evidenceId);
    },
    [fetchDetail],
  );

  /** Re-fetches the currently open detail (server re-mints the media reference). */
  const refreshDetail = useCallback(() => {
    const current = detailState;
    if (current.kind === 'ready') {
      fetchDetail(current.detail.evidence.evidenceId);
      return;
    }
    if (current.kind === 'loading') fetchDetail(current.evidenceId);
  }, [detailState, fetchDetail]);

  const closeDetail = useCallback(() => {
    setDetailState({ kind: 'idle' });
  }, []);

  return {
    form,
    setForm,
    submit,
    reset,
    loadMore,
    state,
    detailState,
    openDetail,
    refreshDetail,
    closeDetail,
  };
}

function mapListResult(result: AdminEvidenceListResult): EvidenceListState {
  switch (result.status) {
    case 200:
      return result.payload === null
        ? { kind: 'error', message: 'Respons daftar bukti tidak valid.' }
        : { kind: 'ready', page: result.payload, loadingMore: false };
    case 401:
      return { kind: 'unauthorized' };
    case 403:
      return { kind: 'blocked', message: result.message ?? 'Akses ditolak.' };
    default:
      return { kind: 'error', message: result.message ?? `HTTP ${result.status}` };
  }
}